import { Button, ButtonProps } from '@/components/ui/Button';
import { Wizard, useWizard } from '@/components/ui/Wizard';
import { cn } from '@/lib/helpers/utils';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { AnimatePresence, Variants, motion } from 'framer-motion';
import React, { useState } from 'react';

interface MatchIntroScreenProps extends React.HTMLAttributes<HTMLDivElement> {
  setShowIntro: Function;
}

const MatchIntroScreen = ({ setShowIntro }: MatchIntroScreenProps) => {
  const [opacity, setOpacity] = useState('opacity-1');
  const [display, setDisplay] = useState(true);

  React.useEffect(() => {
    if (opacity == 'opacity-0') {
      setTimeout(() => {
        setDisplay(false);
      }, 1250);
    }
  }, [opacity]);

  if (!display) {
    return null;
  }

  const screenContents = [
    {
      title: 'Welcome to messages'
    },
    {
      title: 'Hear directly from employers',
      paragraph: `We'll notify you when you receive a new message.`
    },
    {
      title: 'Discover something new',
      paragraph: 'Employers actively look for candidates with key transferable skills.'
    },
    {
      title: 'Help yourself stand out',
      paragraph: 'Having an up-to-date profile will boost your visibility.'
    },
    {
      title: 'Loading your inbox',
      paragraph: 'One sec.',
      animateIcon: true,
      delay: 2000
    }
  ];

  const previousStep = React.useRef<number>(0);

  const NextButton = React.forwardRef<HTMLButtonElement, ButtonProps>(({ children, ...props }, ref) => {
    const { nextStep } = useWizard();

    // if last slide then close intro screens
    if (props.disabled) {
      setTimeout(() => {
        setShowIntro(false);
      }, 2000);
    }

    return (
      <Button ref={ref} icon={true} appearance="primary" variant="outline" onClick={nextStep} {...props}>
        {!children && <ChevronRightIcon className="h-6" />}
        {children}
      </Button>
    );
  });
  NextButton.displayName = 'NextButton';

  const PreviousButton = React.forwardRef<HTMLButtonElement, ButtonProps>(({ children, ...props }, ref) => {
    const { previousStep } = useWizard();
    return (
      <Button ref={ref} icon={true} appearance="primary" variant="outline" onClick={previousStep} {...props}>
        <ChevronLeftIcon className="h-6" />
      </Button>
    );
  });
  PreviousButton.displayName = 'PreviousButton';

  /**
   * Renders out each slide.
   *
   * @param {any} card
   * @param number
   * @return {JSX}
   */
  const createSlide = (card: any, i: number) => {
    return (
      <div key={i} data-cy={`link:intro-screen`} className="slide flex flex-col w-full">
        <div className="flex justify-center w-full">
          <div className="relative left-[-10px]">
            <div className="text-sm h-[128px] w-[128px] bg-white rounded-full top-[10px] relative left-[9px]">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 124 124">
                <path
                  d="M45.5,43.14a5.63,5.63,0,0,0-5.65,5.61V73.54a5.64,5.64,0,0,0,5.65,5.62h9.19v7.28a.71.71,0,0,0,1.12.57l11-7.85H79.19a5.65,5.65,0,0,0,5.66-5.62V48.75a5.64,5.64,0,0,0-5.66-5.61Zm6.83,15.08A2.23,2.23,0,0,1,54.57,56H72.24a2.23,2.23,0,1,1,0,4.45H54.57A2.23,2.23,0,0,1,52.33,58.22Zm2.24,6.2a2.23,2.23,0,1,0,0,4.45H64a2.23,2.23,0,1,0,0-4.45Z"
                  transform="translate(-0.35 0.02)"
                  style={{ fill: '#ff8c44', fillRule: 'evenodd' }}
                />
              </svg>
            </div>
            <div
              className={`text-sm h-[150px] w-[150px] absolute top-0 ${card?.animateIcon ? 'animate-spin' : 'hidden'}`}
            >
              <svg width="150" height="150" viewBox="0 0 150 150" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M75 141.5C111.727 141.5 141.5 111.727 141.5 75C141.5 38.2731 111.727 8.5 75 8.5C38.2731 8.5 8.5 38.2731 8.5 75C8.5 111.727 38.2731 141.5 75 141.5Z"
                  stroke="#FFD1B4"
                  strokeWidth="5"
                />
                <path d="M75 8.5C96.1409 8.5 139.505 22.9868 141.5 75" stroke="#FF8C44" strokeWidth="5" />
              </svg>
            </div>
          </div>
        </div>
        <div className="text-center space-y-5 mt-8 min-h-[10rem]">
          {card?.title && <h1 className="display-title-3xl">{card?.title}</h1>}
          {card?.paragraph && <p className="leading-7 text-xl text-gray-700">{card?.paragraph}</p>}
        </div>
      </div>
    );
  };

  const stepAnimationVariants: Variants = {
    enter: (direction: number) => {
      return {
        x: direction > 0 ? 250 : -250,
        opacity: 0
      };
    },
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => {
      return {
        zIndex: 0,
        x: direction < 0 ? 250 : -250,
        opacity: 0
      };
    }
  };

  interface MatchesIntroWizardStepProps extends React.HTMLAttributes<HTMLDivElement> {
    previousStep: React.MutableRefObject<number>;
  }

  const MatchesIntroWizardStep = React.forwardRef<HTMLDivElement, MatchesIntroWizardStepProps>(
    ({ children, previousStep: previousStepIndex, className }, ref) => {
      const { activeStep } = useWizard();

      React.useEffect(() => {
        return () => {
          previousStepIndex.current = activeStep;
        };
      }, [activeStep, previousStepIndex]);

      return (
        <motion.div
          custom={activeStep - previousStepIndex.current}
          variants={stepAnimationVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            type: 'spring',
            stiffness: 1000,
            damping: 75
          }}
          ref={ref}
          data-cy={`section:onboarding-wizard-stop-${activeStep}`}
        >
          <div className={cn('grid', className)}>{children}</div>
        </motion.div>
      );
    }
  );
  MatchesIntroWizardStep.displayName = 'MatchesIntroWizardStep';

  return (
    <div
      className={`transition-opacity ${opacity} overflow-x-hidden duration-1000 delay-150 top-0 left-0 absolute z-[1] justify-center flex flex-1 flex-col h-full border-4 border-white w-full bg-[url('data:image/svg+xml;base64,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')]`}
    >
      <div className="section-container-full">
        <div className="section-container-2xl">
          <Wizard data-cy={`section:match-intro-slider`} wrapper={<AnimatePresence initial={false} mode="popLayout" />}>
            {screenContents.map((card: any, i: number) => {
              return (
                <MatchesIntroWizardStep previousStep={previousStep} key={`slide${i}`}>
                  {createSlide(card, i)}
                  <div className="mt-4 flex items-center justify-center space-x-4">
                    <PreviousButton disabled={i == 0 && true} />
                    <NextButton disabled={i == 4 && true} />
                  </div>
                </MatchesIntroWizardStep>
              );
            })}
            ;
          </Wizard>
        </div>
      </div>
    </div>
  );
};

export default MatchIntroScreen;
